import { ApiStatus } from '@app/core/models/enums/api-status.enum';

/**
 * What the "Add missing fields" button should do when clicked (FP-16828).
 *
 * The drawer needs three things before it can show anything: a taxonomy in TaaS, that taxonomy's
 * category column, and a database field mapped to it. The gate reports which of those is missing so
 * the FE can open the right popover instead of failing silently.
 */
export enum MissingFieldsGate {
    /** Everything resolved; open the drawer. */
    Ready = 'ready',
    /** TaaS knows the category field but nothing is mapped to it. */
    MapCategoryField = 'map_category_field',
    /** No usable taxonomy/category field on this export. */
    SetCategoryField = 'set_category_field',
    /** Cannot proceed and the user cannot fix it from a popover; explain why. */
    Unsupported = 'unsupported'
}

/**
 * Stable reason codes accompanying a non-ready gate. This is the whole contract for *why* a gate is
 * not ready — the backend sends no copy, and the frontend maps each code to a sentence in
 * {@link MissingFieldsService.gateCopy}.
 */
export enum MissingFieldsGateReason {
    NoDestination = 'no_destination',
    NoKeys = 'no_keys',
    NoCategoryColumn = 'no_category_column',
    TaxonomyNotInTaas = 'taxonomy_not_in_taas',
    NoLiveTaxonomies = 'no_live_taxonomies',
    TaasUnavailable = 'taas_unavailable'
}

export interface MissingFieldsGateResponse {
    /**
     * Present on responses that came from the API. A gate synthesized client-side from a transport
     * failure has no server status, so this is optional rather than fabricated.
     */
    status?: ApiStatus;
    gate: MissingFieldsGate;
    reason?: MissingFieldsGateReason;
    /*
     * The backend also returns `debug_message`: a developer-facing explanation for reading the
     * response directly (curl, network tab, api_logs). It is deliberately NOT declared here so it
     * cannot be bound in a template — user-facing wording comes from {@link MissingFieldsService.gateCopy}.
     */
    /** Export field that carries the category value, per the taxonomy's metadata. */
    category_field?: string;
    /** True when `category_field` is mapped to a database field on this export. */
    mapped?: boolean;
    taxonomy_key?: string;
    taxonomy_version?: string;
}

/**
 * Body for the Add Export (draft) gate + attributes endpoints, which have no saved export to read
 * from and instead receive the in-progress form state.
 */
export interface MissingFieldsDraftContext {
    taxonomy_service_keys: Record<string, string> | string | null;
    export_fields: { export_field_name: string; field_name: string; }[];
    destination: string | null;
}
